import React, { useEffect } from 'react';
import axios from 'axios';
import {
  Icon,
  Tag,
  Space,
  Avatar,
  TableProps,
  EnhancedFilterDropdown,
  message,
  Tooltip,
  theme,
} from 'components';
import styled from 'styled-components';
import { AnsweringSubscriptionTag } from '@bb/domain/dist/types';
import {
  createFormatDateFunction,
  addDatacyToCell,
  addDatacyToTableHeader,
} from 'utils';
import { orgs as searchOrgs } from 'api/SearchController';
import errorReporter from '@app/errorReporter';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import { SearchOrgs } from 'api/SearchController/orgs';
import SearchTable from '../SearchTable';
import { useSearchParams } from '../../AnsweringSearchPage/hooks';
import { getStatusTagColor } from '../../AnsweringSearchPage/utils';
import { useMLSStaffContext } from '../MLSStaffContext';
import { answeringServiceTypesFilters } from '../constants';
import AnsweringServiceTypeTag from '../Tags/AnsweringServiceTypeTag';

const LIMIT = 20;

const assume = (orgId: string) => {
  const chatSessionStorageKeys = [
    'messageDrafts',
    'messageDraftsForNewConvo',
    'chatInstances',
    'activeChatInstance',
    'newConvoSelections',
    'assumedChatInstances',
    'assumedBrokerageId',
  ];

  chatSessionStorageKeys.forEach(key => sessionStorage.removeItem(key));
  window.location.pathname = `/answering/assume/${orgId}`;
};

const MLSStaffBrokeragesTable = () => {
  const {
    text: debouncedText,
    page,
    setPage,
    total,
    setTotal,
    setInitialTotal,
    loading,
    setLoading,
    setCsvLink,
  } = useMLSStaffContext();
  const [searchParam, setSearchParam] = useSearchParams<{
    mlsTypesFilter: string[];
    subscriptionTagsFilter: string[];
    planTypeFilter: string[];
    statusFilter: string[];
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }>();
  const [brokerages, setBrokerages] = React.useState<SearchOrgs['orgs']>();
  const [mlsTypesFilter, setMlsTypesFilter] = React.useState<string[]>(
    searchParam.mlsTypesFilter || [],
  );
  const [subscriptionTagsFilter, setSubscriptionTagsFilter] = React.useState<
    string[]
  >(searchParam.subscriptionTagsFilter || []);
  const [planTypeFilter, setPlanTypeFilter] = React.useState<string[]>(
    searchParam.planTypeFilter || [],
  );
  const [statusFilter, setStatusFilter] = React.useState<string[]>(
    searchParam.statusFilter || [],
  );
  const [sortDirection, setSortDirection] = React.useState<
    'asc' | 'desc' | undefined
  >(searchParam.sortDirection);
  const [sortField, setSortField] = React.useState<string | undefined>(
    searchParam.sortField || 'name',
  );
  const isRendered = React.useRef(false);
  const formatDateStr = createFormatDateFunction('MMM d, yyyy');

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const searchOptions = {
          input: debouncedText,
          from: page * LIMIT,
          sortField: sortField || 'name',
          plans: planTypeFilter,
          statuses: statusFilter,
          sortDir: sortDirection ?? 'asc',
          answeringServiceTypes: subscriptionTagsFilter as (
            | AnsweringServiceType
            | 'ns'
            | 'on'
          )[],
          fetchLockboxCounts: true,
        };

        const _csvLink = axios.getUri({
          url: '/search/orgs',
          method: 'get',
          params: { ...searchOptions, exportCsv: 'true' },
        });
        setCsvLink(_csvLink);

        const {
          orgs,
          count,
          total: totalRes,
        } = await searchOrgs({
          ...searchOptions,
          limit: LIMIT,
          fetchUnconfigured: true,
        });
        setBrokerages(orgs);
        setTotal(count);
        setInitialTotal(totalRes!);
      } catch (e) {
        message.error('Error fetching brokerages data');
        errorReporter.error('Error fetching data', e);
      }
      setLoading(false);
      // Trigger dummy resize to trigger useTableResizer to fetch correct tableHeaderHeight after data loads
      window.dispatchEvent(new Event('resize'));
    })();
  }, [
    debouncedText,
    mlsTypesFilter,
    subscriptionTagsFilter,
    planTypeFilter,
    statusFilter,
    sortDirection,
    sortField,
    page,
  ]);

  useEffect(() => {
    if (!isRendered.current) {
      isRendered.current = true;
      return;
    }
    setPage(0);
    setSearchParam({
      mlsTypesFilter,
      subscriptionTagsFilter,
      planTypeFilter,
      statusFilter,
      sortDirection,
      sortField,
    });
  }, [
    debouncedText,
    mlsTypesFilter,
    subscriptionTagsFilter,
    sortDirection,
    planTypeFilter,
    statusFilter,
    sortField,
  ]);

  const columns: TableProps['columns'] = ([
    {
      width: 200,
      title: 'Brokerage Name',
      key: 'name',
      dataIndex: 'name',
      sorter: true,
      sortOrder:
        sortDirection && sortField === 'name' ? `${sortDirection}end` : null,
      showSorterTooltip: true,
      render: (name: string, { logoUrl }) => (
        <Space align="center" size="middle">
          <Avatar src={logoUrl} />
          {name}
        </Space>
      ),
      onCell: addDatacyToCell('brokerages-name'),
      onHeaderCell: addDatacyToTableHeader('brokerages-name'),
    },
    {
      width: 120,
      title: 'Office IDs',
      key: 'oids',
      dataIndex: 'oids',
      render: (oids: string[]) => oids.join(', '),
      onCell: addDatacyToCell('brokerages-oids'),
      onHeaderCell: addDatacyToTableHeader('brokerages-oids'),
    },
    {
      width: 120,
      title: 'Plan Type',
      key: 'plan-type',
      dataIndex: 'plan',
      filters: [
        {
          text: (
            <>
              Brokerage
              <Tooltip
                title="This brokerage has full access to features on the BrokerBay platform"
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'brokerage',
        },
        {
          text: (
            <>
              Agent
              <Tooltip
                title="This brokerage has a limited view of the features on the BrokerBay platform"
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'agent',
        },
      ],
      filteredValue: planTypeFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      onCell: addDatacyToCell('brokerages-plan-type'),
      onHeaderCell: addDatacyToTableHeader('brokerages-plan-type'),
    },
    {
      width: 120,
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      onCell: addDatacyToCell('brokerages-status'),
      onHeaderCell: addDatacyToTableHeader('brokerages-status'),
      filters: [
        {
          text: (
            <>
              Active
              <Tooltip
                title="The brokerage has fully launched & is active on the BrokerBay platform."
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'active',
        },
        {
          text: (
            <>
              Onboarding
              <Tooltip
                title="The brokerage has selected a launch date but is not yet active on the BrokerBay platform."
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'onboarding',
        },
        {
          text: (
            <>
              Unclaimed
              <Tooltip
                title="This brokerage has yet to be claimed on the BrokerBay platform."
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'unclaimed',
        },
      ],
      filteredValue: statusFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      render: status => (
        <Flex>
          <StyledTag color={getStatusTagColor(status)}>
            {status.toLowerCase()}
          </StyledTag>
        </Flex>
      ),
    },
    {
      width: 130,
      title: 'Concierge',
      key: 'subscriptionTag',
      dataIndex: 'subscriptionTag',
      filters: answeringServiceTypesFilters,
      filteredValue: subscriptionTagsFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      render: answeringServiceType => (
        <AnsweringServiceTypeTag answeringServiceType={answeringServiceType} />
      ),
      onCell: addDatacyToCell('brokerages-subscribed'),
      onHeaderCell: addDatacyToTableHeader('brokerages-subscribed'),
    },
    {
      width: 120,
      sorter: true,
      sortOrder:
        sortDirection && sortField === 'agentCount'
          ? `${sortDirection}end`
          : null,
      showSorterTooltip: true,
      // TODO: Change to Total Users
      title: '# of Agents',
      key: 'number-of-agents',
      dataIndex: 'agentCount',
      onCell: addDatacyToCell('brokerages-agents'),
      onHeaderCell: addDatacyToTableHeader('brokerages-agents'),
    },
    {
      width: 120,
      title: (
        <Tooltip
          placement="top"
          title={
            'This column represents the amount of fully configured listings out of the total listings within this org'
          }
        >
          <Space>Listings Configured</Space>
        </Tooltip>
      ),
      key: 'configured',
      dataIndex: 'unconfigured',
      onCell: addDatacyToCell('configured'),
      onHeaderCell: addDatacyToTableHeader('brokerages-configured'),
      render: ({ unconfiguredCount, totalCount }) => (
        <div>
          {totalCount !== 0
            ? `${totalCount - unconfiguredCount} / ${totalCount} (${
                totalCount
                  ? Math.round(
                      ((totalCount - unconfiguredCount) / totalCount) * 100,
                    )
                  : 0
              }%)`
            : '-'}
        </div>
      ),
    },
    {
      width: 120,
      sorter: false,
      title: 'Lockboxes',
      key: 'number-of-lockboxes',
      dataIndex: 'lockboxTotalCount',
      onCell: addDatacyToCell('brokerages-lockboxes'),
      onHeaderCell: addDatacyToTableHeader('brokerages-lockboxes'),
    },
    {
      width: 120,
      sorter: true,
      sortOrder:
        sortDirection && sortField === 'launchDate'
          ? `${sortDirection}end`
          : null,
      showSorterTooltip: true,
      title: 'Scheduling Launch Date',
      key: 'launch-date',
      dataIndex: 'launchDate',
      onCell: addDatacyToCell('brokerages-launch-date'),
      onHeaderCell: addDatacyToTableHeader('brokerages-launch-date'),
      render: (launchDate: Date) => formatDateStr(launchDate),
    },
    {
      width: 100,
      title: 'Actions',
      key: 'id',
      dataIndex: 'id',
      align: 'right',
      render: (
        id: string,
        { subscriptionTag }: { subscriptionTag: AnsweringSubscriptionTag },
      ) => (
        <Space>
          <ActionText
            onClick={() => assume(id)}
            data-cy={`brokerage-assume-button-${id}`}
          >
            Assume
          </ActionText>
        </Space>
      ),
      onCell: addDatacyToCell('brokerages-assume'),
      onHeaderCell: addDatacyToTableHeader('brokerages-assume'),
    },
  ] as TableProps['columns'])!.filter(Boolean);

  return (
    <SearchTable
      rowKey={record => record.id}
      columns={columns}
      dataSource={brokerages}
      onChange={(_pagination, filters, sorter) => {
        setMlsTypesFilter(filters.mlsTypes as string[]);
        setSubscriptionTagsFilter(filters.subscriptionTag as string[]);
        setPlanTypeFilter(filters['plan-type'] as string[]);
        setStatusFilter(filters.status as string[]);
        setSortField((sorter as any).order && (sorter as any).field);
        setSortDirection(
          (sorter as any).order === 'descend'
            ? 'desc'
            : (sorter as any).order === 'ascend'
              ? 'asc'
              : undefined,
        );
      }}
      // @ts-expect-error
      onRow={brokerage => ({
        'data-cy': `brokerage-row-${brokerage.id}`,
      })}
      loading={loading}
      dataCy={'brokerages-table'}
      totalItems={total}
    />
  );
};

const Flex = styled.div`
  display: flex;
`;
const StyledTag = styled(Tag)`
  text-transfrom: capitalize;
`;

const ActionText = styled.span<{ disabled?: boolean; padLeft?: boolean }>`
  color: ${theme.blueBase};
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  ${props => props.disabled && 'opacity: 0.5; cursor: not-allowed;'}
  ${props => props.padLeft && 'padding-left: 16px;'}
`;

export default MLSStaffBrokeragesTable;
